/**
 * TechWeekView.jsx
 * ─────────────────────────────────────────────────────────────
 * "Team" view: a compact read-only grid showing
 *   rows  = technicians (+ one "Unassigned" row at the bottom)
 *   cols  = days of the current week
 *   cells = sorted job tiles for that tech on that day
 *
 * No drag-and-drop in this view — it is a planning overview only.
 * Clicking a tile opens the JobDetailsPopCard (via task selection).
 */
import React, { useMemo, useState } from 'react';
import { useTasks } from '../../context/TasksContext';
import { useLeads } from '../../context/LeadsContext';
import UnscheduledStagingColumn from './UnscheduledStagingColumn';

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  scheduled:   { bg: 'bg-blue-100',   text: 'text-blue-800',   border: 'border-blue-300'   },
  approved:    { bg: 'bg-slate-100',  text: 'text-slate-700',  border: 'border-slate-300'  },
  en_route:    { bg: 'bg-amber-100',  text: 'text-amber-800',  border: 'border-amber-300'  },
  on_site:     { bg: 'bg-indigo-100', text: 'text-indigo-800', border: 'border-indigo-300' },
  in_progress: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300' },
  completed:   { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300' },
};

const TECH_PALETTE = [
  { bg: 'bg-orange-100', text: 'text-orange-700', bar: 'bg-orange-400' },
  { bg: 'bg-blue-100',   text: 'text-blue-700',   bar: 'bg-blue-400'   },
  { bg: 'bg-purple-100', text: 'text-purple-700',  bar: 'bg-purple-400' },
  { bg: 'bg-teal-100',   text: 'text-teal-700',    bar: 'bg-teal-400'   },
  { bg: 'bg-rose-100',   text: 'text-rose-700',    bar: 'bg-rose-400'   },
  { bg: 'bg-indigo-100', text: 'text-indigo-700',  bar: 'bg-indigo-400' },
];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// ── Helpers ───────────────────────────────────────────────────────────────────

function isSameDay(dateStr, refDate) {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return false;
  return (
    d.getFullYear() === refDate.getFullYear() &&
    d.getMonth()    === refDate.getMonth() &&
    d.getDate()     === refDate.getDate()
  );
}

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function techInitials(name) {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

// ── JobTile ───────────────────────────────────────────────────────────────────

function JobTile({ task, techPalette, onSelect }) {
  const status     = task.status || 'approved';
  const statusCls  = STATUS_COLORS[status] ?? STATUS_COLORS.approved;
  const startTime  = formatTime(task.scheduledStart);
  const clientName = task.clientName || task.title || 'Job';
  const service    = task.serviceType || task.service || '';

  return (
    <button
      type="button"
      onClick={() => onSelect(task)}
      data-testid={`team-job-tile-${task.id}`}
      className={`
        w-full text-left rounded border px-2 py-1.5 text-[10px]
        transition-all duration-100
        hover:shadow-md hover:scale-[1.02] hover:z-10 relative
        ${statusCls.bg} ${statusCls.border} ${statusCls.text}
      `}
    >
      <div className="font-semibold truncate leading-tight">{clientName}</div>
      {service && <div className="truncate text-[9px] opacity-70">{service}</div>}
      {startTime && (
        <div className="text-[9px] opacity-60 mt-0.5">{startTime}</div>
      )}
      {task.isRecurring && (
        <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-emerald-500" title="Recurring" />
      )}
    </button>
  );
}

// ── TechRow ───────────────────────────────────────────────────────────────────

function TechRow({ techName, palette, weekDays, jobsByDay, isUnassigned, onSelect }) {
  const totalJobs = Object.values(jobsByDay).reduce((sum, arr) => sum + arr.length, 0);
  const completedJobs = Object.values(jobsByDay)
    .flat()
    .filter((j) => j.status === 'completed').length;

  return (
    <tr
      className="border-t border-gray-200"
      data-testid={`team-row-${isUnassigned ? 'unassigned' : techName?.replace(/\s+/g, '-').toLowerCase()}`}
    >
      {/* Tech header cell */}
      <td
        className={`
          w-32 min-w-[7rem] px-2 py-3 align-top sticky left-0 z-10
          bg-white border-r border-gray-200
        `}
      >
        <div className="flex items-center gap-2">
          {isUnassigned ? (
            <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 text-[10px] font-bold flex-shrink-0">
              ?
            </div>
          ) : (
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${palette.bg} ${palette.text}`}>
              {techInitials(techName)}
            </div>
          )}
          <div className="min-w-0">
            <p className="text-xs font-semibold text-gray-800 leading-tight truncate">
              {isUnassigned ? 'Unassigned' : techName}
            </p>
            {totalJobs > 0 && (
              <p className="text-[9px] text-gray-400 leading-none">
                {completedJobs}/{totalJobs} done
              </p>
            )}
          </div>
        </div>
      </td>

      {/* Day cells */}
      {weekDays.map((day, di) => {
        const jobs = jobsByDay[di] || [];
        return (
          <td
            key={di}
            className="px-1.5 py-1.5 align-top min-w-[100px] max-w-[160px] border-r border-gray-100 last:border-r-0"
          >
            {jobs.length > 0 ? (
              <div className="space-y-1">
                {jobs.map((job) => (
                  <JobTile
                    key={job.id}
                    task={job}
                    techPalette={palette}
                    onSelect={onSelect}
                  />
                ))}
              </div>
            ) : (
              <div className="h-8 flex items-center justify-center">
                <span className="text-[10px] text-gray-300">—</span>
              </div>
            )}
          </td>
        );
      })}
    </tr>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

const TechWeekView = ({ weekStart, onJobSelect }) => {
  const { tasks }  = useTasks();
  const { leads }  = useLeads();

  // Build an array of the 7 day dates for this week
  const weekDays = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + i);
      d.setHours(0, 0, 0, 0);
      return d;
    }),
    [weekStart],
  );

  // Filter to scheduled job tasks only
  const scheduledJobs = useMemo(() =>
    (tasks || []).filter((t) => t.type === 'job' && t.scheduledStart),
    [tasks],
  );

  // Gather all unique technicians from this week's jobs
  const techMap = useMemo(() => {
    const map = new Map(); // uid → name
    scheduledJobs.forEach((t) => {
      if (!t.assignedTo) return;
      const start = new Date(t.scheduledStart);
      const inWeek = weekDays.some((d) => isSameDay(t.scheduledStart, d));
      if (!inWeek) return;
      if (!map.has(t.assignedTo)) {
        map.set(t.assignedTo, t.assignedToName || t.assignedTo);
      }
    });
    return map;
  }, [scheduledJobs, weekDays]);

  const techList = useMemo(() =>
    [...techMap.entries()].map(([uid, name]) => ({ uid, name })),
    [techMap],
  );

  // Group jobs: per tech, per day index
  const buildJobsByDay = (filterFn) =>
    Object.fromEntries(
      weekDays.map((day, di) => [
        di,
        scheduledJobs
          .filter((t) => filterFn(t) && isSameDay(t.scheduledStart, day))
          .sort((a, b) => new Date(a.scheduledStart) - new Date(b.scheduledStart)),
      ]),
    );

  const techJobsByDayMap = useMemo(() =>
    Object.fromEntries(
      techList.map(({ uid }) => [uid, buildJobsByDay((t) => t.assignedTo === uid)])
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scheduledJobs, weekDays, techList],
  );

  const unassignedByDay = useMemo(
    () => buildJobsByDay((t) => !t.assignedTo),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scheduledJobs, weekDays],
  );

  const hasUnassigned = Object.values(unassignedByDay).some((arr) => arr.length > 0);

  // Today index for highlight
  const todayDayIndex = useMemo(() => {
    const now = new Date();
    return weekDays.findIndex((d) => isSameDay(d.toISOString(), now));
  }, [weekDays]);

  // Day header label
  const dayLabel = (d) =>
    d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <div className="flex gap-4 mt-4 h-[calc(100vh-220px)]" data-testid="tech-week-view">
      {/* Main grid */}
      <div className="flex-1 bg-white border border-gray-200 rounded-md overflow-auto">
        <table className="w-full border-collapse text-xs" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '8rem' }} />
            {weekDays.map((_, i) => <col key={i} />)}
          </colgroup>

          {/* Header row */}
          <thead>
            <tr className="bg-gray-50 sticky top-0 z-20 border-b border-gray-200">
              <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 uppercase tracking-wide sticky left-0 bg-gray-50 z-30 border-r border-gray-200">
                Technician
              </th>
              {weekDays.map((day, di) => {
                const isToday = di === todayDayIndex;
                return (
                  <th
                    key={di}
                    className={`
                      px-2 py-2 text-center text-[10px] font-semibold uppercase tracking-wide
                      border-r border-gray-100 last:border-r-0
                      ${isToday ? 'text-[var(--cb-orange)] bg-[rgba(242,107,29,0.05)]' : 'text-gray-500'}
                    `}
                  >
                    {dayLabel(day)}
                    {isToday && (
                      <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-[var(--cb-orange)] align-middle" />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {/* Tech rows */}
            {techList.length === 0 && !hasUnassigned ? (
              <tr>
                <td colSpan={8} className="px-4 py-12 text-center text-sm text-gray-400">
                  No scheduled jobs this week
                </td>
              </tr>
            ) : (
              <>
                {techList.map(({ uid, name }, idx) => (
                  <TechRow
                    key={uid}
                    techName={name}
                    palette={TECH_PALETTE[idx % TECH_PALETTE.length]}
                    weekDays={weekDays}
                    jobsByDay={techJobsByDayMap[uid] || {}}
                    isUnassigned={false}
                    onSelect={onJobSelect}
                  />
                ))}

                {/* Unassigned row — only shown when there are unassigned jobs */}
                {hasUnassigned && (
                  <TechRow
                    key="unassigned"
                    techName="Unassigned"
                    palette={{ bg: 'bg-gray-100', text: 'text-gray-500', bar: 'bg-gray-400' }}
                    weekDays={weekDays}
                    jobsByDay={unassignedByDay}
                    isUnassigned
                    onSelect={onJobSelect}
                  />
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Staging column (unscheduled approved leads) */}
      <UnscheduledStagingColumn leads={leads} tasks={tasks} />
    </div>
  );
};

export default TechWeekView;
